<script>
// Card "Evolução do patrimônio · ciclo de vida" — projeta o patrimônio do
// cliente por idade (acumulação até a aposentadoria, retirada anual fixa
// depois), comparando Comissionado vs Consultor CVM. Motor: engine.
// simulateEvolucaoPatrimonio + <line-chart> (Chart.js v2, mesmo padrão de
// fee-lab-projecao-chart.vue). A linha vertical de aposentadoria é
// desenhada pelo plugin global registrado em @plugins/chartjs.js, ativado
// via chartOptions.retirementLine.

import { formatLines } from '@utils/GraficoData'
import { fmtBRLk } from '@modules/fee-fixo/utils/fee-lab-format'
import { toneBySign } from '@modules/fee-fixo/utils/Utils'
import { simulateEvolucaoPatrimonio } from '@modules/fee-fixo/utils/fee-lab-engine'
import {
  REGIME_CHART_COLORS,
  CHART_STYLES,
} from '@modules/fee-fixo/constants/feeLab'

// Área sob a curva "Consultor CVM" — REGIME_CHART_COLORS.cons (#56AF79) em
// 15% de opacidade (rgb(86,175,121) é o mesmo hex decomposto). Sem isso,
// formatLines usa a cor sólida também no fill (backgroundColor = borderColor),
// e uma área grande no verde cheio fica pesada/escura demais — a linha
// continua no verde sólido, só o preenchimento é clareado.
const CONS_AREA_FILL = 'rgba(86, 175, 121, 0.15)'

export default {
  name: 'FeeLabEvolucaoPatrimonio',
  props: {
    state: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chartStyles: CHART_STYLES,
      toneStyles: {
        pos: { color: 'var(--success)' },
        neg: { color: 'var(--danger)' },
      },
    }
  },
  computed: {
    resultado() {
      return simulateEvolucaoPatrimonio(this.state)
    },
    idadeFinal() {
      const ages = this.resultado.ages
      return ages[ages.length - 1]
    },
    subtitulo() {
      const r = this.resultado
      return (
        'Dos ' +
        r.ages[0] +
        ' aos ' +
        this.idadeFinal +
        ' anos · aportes até ' +
        r.idadeAposentadoria +
        ', depois retirada de ' +
        fmtBRLk(this.state.retiradaAnual) +
        '/ano'
      )
    },
    deltaLabel() {
      return fmtBRLk(this.resultado.delta)
    },
    deltaTone() {
      return toneBySign(this.resultado.delta)
    },
    hasData() {
      return this.resultado.ages.length > 1
    },
    chartData() {
      const r = this.resultado
      const dados = {
        mode: 'R$',
        labels: r.ages.map(String),
        data: [
          {
            label: 'Comissionado',
            dados: r.comis,
            color: REGIME_CHART_COLORS.comis,
            chartOptions: { borderDash: [6, 4] },
          },
          {
            label: 'Consultor CVM',
            dados: r.cons,
            color: REGIME_CHART_COLORS.cons,
            chartOptions: { fill: 'origin', backgroundColor: CONS_AREA_FILL },
          },
        ],
      }
      const { chartOptions, chartData } = formatLines(dados)
      chartOptions.maintainAspectRatio = false
      chartOptions.retirementLine = {
        age: r.idadeAposentadoria,
        label: 'Aposentadoria · ' + r.idadeAposentadoria,
      }
      return { chartOptions, chartData }
    },
    chartOptions() {
      return this.chartData.chartOptions
    },
    chartDataset() {
      return this.chartData.chartData
    },
    kpis() {
      const r = this.resultado
      return [
        {
          key: 'pico',
          label: 'Pico de patrimônio (Consultor CVM)',
          value: fmtBRLk(r.picoCons),
        },
        {
          key: 'comis',
          label: 'Patrimônio aos ' + this.idadeFinal + ' · comissionado',
          value: fmtBRLk(r.comisFinal),
        },
        {
          key: 'fee',
          label: 'Patrimônio aos ' + this.idadeFinal + ' · fee-based',
          value: fmtBRLk(r.consFinal),
        },
      ]
    },
  },
}
</script>

<template>
  <div class="fee-lab-evolucao az-card-bordered rounded-sm">
    <div
      class="d-flex align-center justify-space-between flex-wrap az-gap-small mb-1"
    >
      <div>
        <h3 class="az-text-primary az-size-14 font-semibold">
          Evolução do patrimônio · ciclo de vida
        </h3>
        <p class="az-text-small ma-0 mt-1">{{ subtitulo }}</p>
      </div>
      <div class="fee-lab-evolucao__delta">
        <span class="az-text-small">Δ patrimônio aos {{ idadeFinal }} anos</span>
        <span class="az-title-secondary" :style="toneStyles[deltaTone]">
          {{ deltaLabel }}
        </span>
      </div>
    </div>

    <div v-if="hasData" class="fee-lab-evolucao__canvas mt-4">
      <line-chart
        :chart-data="chartDataset"
        :options="chartOptions"
        :styles="chartStyles"
      />
    </div>

    <div class="fee-lab-evolucao__kpis mt-4">
      <div
        v-for="kpi in kpis"
        :key="kpi.key"
        class="fee-lab-evolucao__kpi az-card-bordered rounded-sm az-gap-xsmall"
      >
        <span class="az-text-small">{{ kpi.label }}</span>
        <span class="az-title-secondary">{{ kpi.value }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.fee-lab-evolucao__delta
  display: flex
  flex-direction: column
  align-items: flex-end

.fee-lab-evolucao__canvas
  position: relative
  height: 260px

.fee-lab-evolucao__kpis
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr))
  gap: 12px

.fee-lab-evolucao__kpi
  display: flex
  flex-direction: column
  // Fundo #FAFAFA (token, não hex) — mesmo padrão de
  // fee-lab-composicao-custo.vue (__box), não branco puro.
  background: var(--neutral-50)
</style>
